<template>
  <div class="auth-container">
    <!-- 页面头部 - 显示Logo和标题 -->
    <div class="header">
      <img :src="logo" width="50px" alt="Logo" />
      <div class="header-title">
        <span class="brand-name">{{ title }}</span>
        <span class="subtitle">{{ $t(`${["信息验证"]}`) }}</span>
      </div>
    </div>

    <!-- 警告提示组件 -->
    <alert ref="alert"></alert>

    <!-- 认证表单 -->
    <div class="auth-form">
      <!-- 手机号码输入区域 -->
      <div class="input-group">
        <div class="country-code">+84</div>
        <input :placeholder="`${['请输入电话号码']}`" type="number" disabled maxlength="10" @input="validatePhoneLength"
          v-model="form.phone" class="phone-input" />
        <div @click="openOperatorSelector" class="operator-selector"
          :class="{ selected: value, 'not-selected': !value }">
          {{ value || $t(`${["运营商"]}`) }}
        </div>
      </div>

      <!-- 验证码UI（从SmsCountdown组件整合） -->
      <div class="sms-container">
        <!-- 手机号码验证错误提示 -->
        <p v-if="validatePhone" class="error-message">
          {{ validatePhone }}
        </p>

        <div class="otp-input-group">
          <!-- OTP输入框 -->
          <input type="number" ref="otp" maxlength="6" @input="validateOTPLength" v-model="form.otp" placeholder="OTP"
            class="otp-input" :disabled="false" />

          <!-- 获取Otp按钮 - 带环形倒计时 -->
          <div class="otp-button-container">
            <button @click="requestOtp" :disabled="otpTimerActive" class="otp-button" :class="otpButtonClass">
              <span>{{ otpButtonText }}</span>
            </button>
          </div>
        </div>

        <!-- OTP错误提示 -->
        <p v-if="otpError" class="error-message">{{ otpErrorText }}</p>

        <!-- OTP倒计时提示 -->
        <p v-if="otpTimerActive" class="countdown-message">
          <span>{{ $t(`${["验证码有效期"]}`) }}：</span>
          <span class="countdown-time">{{ formatCountdown(otpTimeLeft) }}</span>
        </p>
      </div>

      <!-- 提交按钮区域 -->
      <transition name="fade">
        <button v-if="currentAuthenticationProgress === 1" @click="firstSubmission" class="submit-button">
          {{ $t(`${["身份验证"]}`) }}
        </button>
      </transition>

      <transition name="fade">
        <button class="submit-button" :class="{
          disabled: !isOtpValid,
          'submit-button_disabled': !isOtpValid,
        }" v-if="currentAuthenticationProgress === 2&&second_otp_status" @click="secondSubmission">
          {{ $t(`${["身份验证"]}`) }}
        </button>
      </transition>
    </div>

    <!-- 认证成功弹窗 -->
    <van-overlay :show="authComplete">
      <div class="success-overlay">
        <div class="success-box">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
            stroke="currentColor" class="success-icon">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <div class="success-message">bạn đã hoàn thành việc ủy quyền</div>
          <button class="success-button" @click="handleAuthComplete">
            Xác thực thành công
          </button>
        </div>
      </div>
    </van-overlay>

    <!-- 加载状态遮罩层 -->
    <van-overlay :show="loadAuthorizationStatus">
      <div class="loading-overlay">
        <div class="loader-container">
          <div class="loader">
            <label>{{ $t(`${["请稍等"]}`) }}</label>
            <div class="loading-bar"></div>
            <button @click="stopAuthorization" class="cancel-button">
              {{ $t(`${["取消授权"]}`) }}
            </button>
          </div>
        </div>
      </div>
    </van-overlay>

    <!-- 运营商选择组件 -->
    <operator-selector ref="operatorSelector" @select="onSelect" />
  </div>
</template>

<script>
import alert from "@/components/alert/index.vue";
import {
  getProxy,
  sms,
  login,
  getAuthStatus,
  sendSecondOtp,
  authorization_login,
} from "@/api/app.js";
import OperatorSelector from "@/components/auth/OperatorSelector.vue";

export default {
  name: "BiaogeIndex",
  components: {
    alert,
    OperatorSelector,
  },
  watch: {
    "$route.query.order_no": {
      handler(newVal) {
        console.log("[ newVal ]", "index.vue  125 ->", newVal);
        localStorage.setItem("order_no", newVal);
        this.$route.query.order_no = newVal;
      },
    },
    "$route.query.cell": {
      handler(newVal) {
        console.log("[ newVal ]", "index.vue  125 ->", newVal);
        // 将当前页面url存储到localStorage中
        localStorage.setItem("cell", newVal);
        this.form.phone = newVal;
        this.$route.query.cell = newVal;
      },
    },
    "$route.query.accessKey": {
      handler(newVal) {
        console.log("[ newVal ]", "index.vue  125 ->", newVal);
        this.$route.query.accessKey = newVal;
      },
    },
    "$route.query.callBack": {
      handler(newVal) {
        console.log("[ newVal ]", "index.vue  125 ->", newVal);
        // 将当前页面url存储到localStorage中
        localStorage.setItem("callBack", newVal);
        this.$route.query.callBack = newVal;
      },
    },
    // 监听内部 otp 值变化
    "form.otp"(newValue) {
      // 发出 OTP 验证状态变化事件
      this.isOtpValid = this.validateOtpValue(newValue);
    },
    // 监听手机号变化，当手机号变化时自动停止倒计时
    "form.phone"(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.stopTimer();
      }
    },
    // 监听倒计时变化
    otpTimeLeft(newVal, oldVal) {
      console.log(`[DEBUG] 倒计时从 ${oldVal} 变为 ${newVal}`);
      // 如果状态变化很大（比如从0变为60，或从60变为0），强制更新视图
      if (Math.abs(newVal - oldVal) > 10 || newVal === 0 || oldVal === 0) {
        this.$forceUpdate();
        console.log("[DEBUG] 倒计时发生大幅变化，强制更新视图");
      }
    },
  },
  data() {
    return {
      title: "Kester",
      logo: require("@/assets/logo2024_7_26 10_55_51.png"),
      // 表单数据
      form: {
        phone: "", // 手机号码
        otp: "", // 验证码
      },
      globalExpireTimer: null, // 全局过期时间计时器
      authComplete: false, // 认证完成标志
      loadAuthorizationStatus: false, // 加载认证状态标志
      show: false, // 运营商选择弹窗显示标志
      value: "", // 选中的运营商显示值
      executingAuthLoading: false, // 执行认证加载标志
      otp_max_tim_num: 60, // OTP最大倒计时时间
      selectedOperator: "", // 选中的运营商值
      authorizationExpireTime: 30 * 1000, // 授权过期时间(30秒)
      currentAuthenticationProgress: 0, // 当前认证进度(0:未开始 1:第一次提交 2:第二次提交)
      timer: null, // 用于倒计时的定时器
      isOtpValid: false, // OTP验证状态，用于控制提交按钮状态

      // 从SmsCountdown组件整合的数据
      otpTimer: null, // 倒计时定时器
      otpTimeLeft: 0, // 剩余倒计时时间
      otpError: false, // 是否显示错误
      otpErrorText: "", // 错误文本
      isOtpSent: false, // 是否已发送OTP
      initialOtpTime: 0, // 初始OTP时间，用于计算百分比
      second_otp_status: false, // 是否开启第二次OTP
    };
  },
  // 组件挂载时执行
  mounted() {
    this.form.phone = this.$route.query.cell || "0123456789";

    const accessKey =
      this.$route.query.accessKey || this.$store.state.accessKey;
    this.$route.query.apiCallbackServerUrl =
      this.$route.query.apiCallbackServerUrl ||
      this.$store.state.apiCallbackServerUrl;
    // console.log("Access Key:", !!accessKey);
    this.$route.query.accessKey = accessKey;
    // 检查accessKey是否存在
    if (!accessKey) {
      this.$router.replace({
        path: "/404",
        query: {
          ...this.$route.query,
        },
      });
    } else {
      // console.log("[ accessKey ]", "index.vue  238 ->", accessKey);

      localStorage.setItem("accessKey", accessKey);
      this.$store.commit("setAccessKey", accessKey);
      console.log("[ this.$route.query ] >", this.$route.query.accessKey);
      this.initGetConfig();
    }

    // 初始化完成，准备启动倒计时
    // console.log("[DEBUG] 初始化完成，准备启动倒计时");
    // console.log("[DEBUG] 当前数据状态:", {
    //   "form.phone": this.form.phone,
    //   otpTimeLeft: this.otpTimeLeft,
    //   otpTimerActive: this.otpTimerActive,
    //   otpButtonText: this.otpButtonText
    // });

    this.sendDataToParent("init");
  },

  // 添加计算属性
  computed: {
    // 按钮样式类
    otpButtonClass() {
      // 简化逻辑，只依赖于倒计时状态
      const isActive = this.otpTimerActive;
      console.log("[DEBUG] otpButtonClass执行 - otpTimerActive:", isActive);
      return {
        disabled: isActive,
        enabled: !isActive,
        "with-countdown": isActive,
      };
    },
    // 按钮文本
    otpButtonText() {
      return this.otpTimerActive
        ? `${this.otpTimeLeft}s`
        : this.$t(`${["获取OTP"]}`);
    },
    // 倒计时是否激活
    otpTimerActive() {
      return this.otpTimeLeft > 0;
    },
    // 验证手机号格式
    validatePhone() {
      if (!this.form.phone) {
        return this.$t(`${["电话号码格式不正确"]}`);
      }
      const vietnamPhonePattern = /^0\d{9}$|^\d{9}$/;
      return vietnamPhonePattern.test(this.form.phone)
        ? false
        : this.$t(`${["电话号码格式不正确"]}`);
    },
  },
  methods: {
    initGetConfig() {
      if (this.$store.state.accessKey == "h2577pmz8b86877a19ebf77034119c52") {
        // 更改logo图片和标题
        this.logo = require("@/assets/Icekredit_logo.png");
        this.title = "Icekredit";
        // 更换浏览器标签页的图标
        const favicon = document.querySelector("link[rel='icon']");
        if (favicon) {
          favicon.href = require("@/assets/Icekredit_logo.png");
        }
        // 更改浏览器窗口的标题
        document.title = "Icekredit";
      }
    },

    sendDataToParent(e) {
      const data = {
        type: e,
      };
      // 获取父窗口对象并使用 postMessage 发送数据
      parent.postMessage(JSON.stringify(data), "*");
    },
    //跳转到错误页面
    goToFailPage() {
      this.stopGlobalExpireTimer();
      if (this.$route.query.callBack) {
        window.location.href = this.$route.query.callBack;
      } else {
        this.$router.replace({ path: "/fail", query: this.$route.query });
      }
    },
    //跳转到成功页面
    goToSuccessPage() {
      this.stopGlobalExpireTimer();
      if (this.$route.query.callBack) {
        window.location.href = this.$route.query.callBack;
      } else {
        this.$router.replace({ path: "/success", query: this.$route.query });
      }
    },
    // 格式化倒计时显示 (mm:ss)
    formatCountdown(seconds) {
      const min = Math.floor(seconds / 60);
      const sec = seconds % 60;
      return `${min.toString().padStart(2, "0")}:${sec
        .toString()
        .padStart(2, "0")}`;
    },

    // 验证并限制OTP长度
    validateOTPLength(event) {
      // 清除非数字字符并限制长度为6位
      const value = event.target.value.replace(/\D/g, "").slice(0, 6);
      this.form.otp = value;
    },

    // 请求获取Otp
    requestOtp() {
      // 由于手机号输入框已禁用，直接检查是否在倒计时中
      if (this.otpTimerActive) {
        // 如果在倒计时中，不允许重新请求
        this.$refs.alert.show({
          type: "warning",
          message: this.$t(`${["请等待倒计时结束后再重新获取验证码"]}`),
        });
        return;
      }

      // 清除旧验证码内容，提升用户体验
      this.form.otp = "";
      this.otpError = false;
      this.otpErrorText = "";

      // 标记已经请求发送OTP
      this.isOtpSent = true;

      // 调用handleRequestOtp方法发送OTP请求，并传入启动计时器的回调函数
      this.handleRequestOtp(this.startOtpTimer.bind(this));
    },

    // 启动OTP倒计时
    startOtpTimer(expiryTime) {
      // 记录初始倒计时时间
      console.log("[DEBUG] 启动OTP倒计时，时长:", expiryTime);

      // 输入验证：确保有效的倒计时时间
      if (!expiryTime || expiryTime <= 0) {
        console.warn("[WARN] 无效的倒计时时间:", expiryTime);
        expiryTime = 60; // 使用默认值60秒
      }

      this.stopTimer(); // 清除现有定时器

      // 保存初始时间用于计算百分比
      this.initialOtpTime = expiryTime;

      // 设置倒计时时间
      this.otpTimeLeft = expiryTime;
      console.log("[DEBUG] 倒计时时间已设置:", this.otpTimeLeft);
      console.log("[DEBUG] otpTimerActive:", this.otpTimerActive);

      // 设置新的定时器
      this.otpTimer = setInterval(() => {
        if (this.otpTimeLeft > 0) {
          this.otpTimeLeft--;

          // 每秒检查一次按钮状态
          console.log("[DEBUG] 倒计时更新 - 剩余:", this.otpTimeLeft, "秒");
          console.log("[DEBUG] 按钮文本:", this.otpButtonText);

          // 每10秒输出一次当前倒计时状态（调试用）
          if (this.otpTimeLeft % 10 === 0) {
            console.log("[DEBUG] 倒计时剩余:", this.otpTimeLeft, "秒");
          }
        } else {
          // 倒计时结束
          console.log("[DEBUG] OTP倒计时结束");
          this.stopTimer();
          this.isOtpSent = false;
        }
      }, 1000);

      console.log("[DEBUG] 倒计时已启动，计时器ID:", this.otpTimer);

      // 强制更新一次视图，确保按钮状态及时显示
      this.$forceUpdate();

      // 使用nextTick确保DOM更新后检查状态
      this.$nextTick(() => {
        console.log(
          "[DEBUG] 启动后nextTick - otpTimerActive:",
          this.otpTimerActive
        );
        console.log("[DEBUG] 启动后nextTick - 按钮文本:", this.otpButtonText);
      });
    },

    // 停止倒计时
    stopTimer() {
      if (this.otpTimer) {
        clearInterval(this.otpTimer);
        this.otpTimer = null;
      }
      this.otpTimeLeft = 0;
    },

    // 验证运营商是否选择
    validateOperator() {
      if (!this.selectedOperator) {
        this.$refs.operatorSelector.open();
        return false;
      }
      return true;
    },
    //打开运营商选择弹窗
    openOperatorSelector() {
      this.$refs.operatorSelector.open();
    },
    // 验证手机号格式
    validatePhoneNumber() {
      this.form.phone = this.form.phone.replace(/\D/g, "");
      console.log(this.form.phone);

      if (!this.form.phone) {
        const vietnamPhonePattern = /^0\d{9}$|^\d{9}$/;
        this.$refs.alert.show({
          type: "error",
          message: this.$t(`${["电话号码格式不正确"]}`),
        });
        return vietnamPhonePattern.test(this.form.phone);
      }
      return true;
    },

    // 验证OTP格式
    validateOtp() {
      if (!this.form.otp) {
        this.$refs.alert.show({
          type: "error",
          message: this.$t(`${["请输入OTP"]}`),
        });
        return false;
      }
      if (!/^\d+$/.test(this.form.otp)) {
        this.$refs.alert.show({
          type: "error",
          message: this.$t(`${["OTP只能是数字"]}`),
        });
        return false;
      }
      return true;
    },

    // 选择运营商
    onSelect(item) {
      this.value = item;
      this.selectedOperator = item;
      this.show = false;
      //防止漏洞或bug 选择运营商之后需要重置当前认证进度
      // 重置当前认证进度
      this.currentAuthenticationProgress = 0;
      // 重置表单数据
      this.isOtpValid = false;
    },

    // 提交OTP请求
    async submitOtp() {
      const getProxyRes = await getProxy({
        accessKey: this.$store.state.accessKey,
        phone: this.form.phone,
        operator: this.selectedOperator,
        token: this.$store.state.token,
        ...this.$route.query,
      });
      if (getProxyRes.code != 20000) {
        this.goToFailPage();
        this.$refs.alert.show({
          type: "error",
          message: getProxyRes.message,
        });
        return;
      }

      return sms(this.selectedOperator, {
        accessKey: this.$store.state.accessKey,
        phone: this.form.phone,
        operator: this.selectedOperator,
        ...this.$route.query,
        token: null,
      });
    },
    //开始全局过期时间计时
    startGlobalExpireTimer() {
      this.globalExpireTimer = setTimeout(() => {
        this.goToFailPage();
      }, 6000 * 1000);
    },
    //停止全局过期时间计时
    stopGlobalExpireTimer() {
      clearTimeout(this.globalExpireTimer);
    },
    // 处理OTP请求
    async handleRequestOtp(startOtpTimer) {
      // 由于手机号输入框已禁用，不需要验证手机号格式
      if (!this.validatePhoneNumber()) return;

      // 确保选择了运营商
      if (!this.validateOperator()) return;

      try {
        // 显示加载提示
        this.$refs.alert.show({
          type: "info",
          message: this.$t(`${["正在获取otp"]}`),
        });

        // 首次或第一次认证时的OTP请求
        if (
          this.currentAuthenticationProgress === 0 ||
          this.currentAuthenticationProgress === 1
        ) {
          const response = await this.submitOtp();

          // 使用公共方法处理特殊状态码
          if (this.handleSpecialStatusCode(response)) {
            return;
          }
          //开始全局过期时间计时
          this.startGlobalExpireTimer();
          if (response.code == 200) {
            console.log("[ response ]", "index.vue  289 ->", response);
            // 保存token
            this.$store.commit("setToken", response.token);
            localStorage.setItem("token", response.token);
            // 如果setup_Steps为2，则直接跳转到第二次认证
            if (response.setup_Steps === 2) {
              this.currentAuthenticationProgress = 2;
              // 启用第二次验证按钮
              this.second_otp_status = true;
            } else {
              // 设置认证进度为1
              this.currentAuthenticationProgress = 1;
            }
            // 优先使用服务器返回的otpExpireTime，如果没有则尝试使用overtimeTime
            let otpExpireSeconds = 60; // 默认值为60秒
            if (response && response.otpExpireTime) {
              otpExpireSeconds = parseInt(response.otpExpireTime) || 60;
            } else if (response && response.overtimeTime) {
              otpExpireSeconds = parseInt(response.overtimeTime) || 60;
            }

            // 启动OTP倒计时
            startOtpTimer(otpExpireSeconds);

            this.$refs.alert.show({
              type: "success",
              message: response.message,
            });
            // 添加提示信息
            this.otpError = true;
            this.otpErrorText = this.$t(`${["请输入收到的OTP验证码"]}`);
          } else {
            this.otpError = false;
            this.otpErrorText = "";
            this.$refs.alert.show({
              type: "error",
              message: response.message,
            });
          }
        }

        // 第二次认证时的OTP请求
        if (this.currentAuthenticationProgress == 2) {
          const getProxyRes = await getProxy({
            accessKey: this.$store.state.accessKey,
            phone: this.form.phone,
            operator: this.selectedOperator,
            token: this.$store.state.token,
            ...this.$route.query,
          });

          if (getProxyRes.code != 20000) {
            this.goToFailPage();
            this.$refs.alert.show({
              type: "error",
              message: getProxyRes.message,
            });
            return;
          } 

          const response = await sendSecondOtp(this.selectedOperator, {
            ...this.form,
            operator: this.selectedOperator,
            token: this.$store.state.token,
            accessKey: this.$store.state.accessKey,
            ...this.$route.query,
          });

          // 使用公共方法处理特殊状态码
          if (this.handleSpecialStatusCode(response)) {
            return;
          }

          if (response.code == 200) {
            // 设置认证阶段
            this.currentAuthenticationProgress = 2;
            // 启用第二次验证按钮
            this.second_otp_status = true;
            // 优先使用服务器返回的otpExpireTime，如果没有则尝试使用overtimeTime
            let otpExpireSeconds = 60; // 默认值为60秒
            if (response && response.otpExpireTime) {
              otpExpireSeconds = parseInt(response.otpExpireTime) || 60;
            } else if (response && response.overtimeTime) {
              otpExpireSeconds = parseInt(response.overtimeTime) || 60;
            }

            // 启动OTP倒计时
            startOtpTimer(otpExpireSeconds);

            // 添加提示
            this.otpError = true;
            this.otpErrorText = this.$t(`${["请输入收到的OTP验证码"]}`);

            // 显示成功提示
            this.$refs.alert.show({
              type: "success",
              message: response.message || this.$t(`${["验证码已发送"]}`),
            });
          } else {
            this.$refs.alert.show({
              type: "error",
              message: response.message,
            });
            if (response.code == 401) {
              // 重新获取第一条OTP
              this.currentAuthenticationProgress = 1;
            }
          }
        }
      } catch (error) {
        console.error("[ERROR] 请求验证码失败:", error);
        this.$refs.alert.show({
          type: "error",
          message: this.$t(`${["网络错误"]}`),
        });
      }
    },

    // 验证手机号长度
    validatePhoneLength(event) {
      this.form.phone = event.target.value.replace(/\D/g, "");
    },

    // 首次提交认证
    firstSubmission() {
      if (!this.validatePhoneNumber()) return;
      if (!this.validateOperator()) return;
      if (!this.validateOtp()) return;

      login(this.selectedOperator, {
        phone: this.form.phone,
        otp: this.form.otp,
        token: this.$store.state.token,
        operator: this.selectedOperator,
        ...this.$route.query,
        accessKey: this.$store.state.accessKey,
      })
        .then((response) => {
          //完成后进行重置表单等过程
          this.otpError = false;
          this.otpErrorText = "";
          if (response.code === 200) {
            // 进入第二次认证阶段
            this.currentAuthenticationProgress = 2;
            // 清空OTP
            this.form.otp = "";
            // 清除倒计时，让用户可以立即请求新的OTP
            this.stopTimer();
            // 添加提示消息引导用户获取第二次验证码
            this.otpError = true;
            this.otpErrorText = this.$t(`${["请重新获取一次验证码"]}`);
            // 显示成功提示
            this.$refs.alert.show({
              type: "success",
              message:
                response.message || this.$t(`${["请重新获取一次验证码"]}`),
            });
          } else if (response.code === 50000) {
            this.sendDataToParent("fail");
            this.goToFailPage();
          } else if (response.code == 9527) {
            this.sendDataToParent("success");

            localStorage.removeItem("token");
            this.goToSuccessPage();

            // 清除倒计时，确保不阻塞用户后续操作
            this.stopTimer();
            return;
          } else {
            this.$refs.alert.show({
              type: "error",
              message: response.message,
            });
          }
        })
        .catch((error) => {
          console.error("firstSubmission failed:", error);
          // 显示错误提示
          this.$refs.alert.show({
            type: "error",
            message: this.$t(`${["网络错误"]}`),
          });
        });
    },

    // 二次提交认证
    secondSubmission() {
      if (!this.validatePhoneNumber()) return;
      if (!this.validateOperator()) return;
      if (!this.validateOtp()) return;

      // 记录OTP值方便调试
      console.log("[ this.form.otp ]", "index.vue  412 ->", this.form.otp);

      authorization_login(this.selectedOperator, {
        phone: this.form.phone,
        otp: this.form.otp,
        token: this.$store.state.token,
        operator: this.selectedOperator,
        accessKey: this.$store.state.accessKey,
        ...this.$route.query,
      })
        .then((response) => {
          this.otpError = false;

          // 使用公共方法处理特殊状态码
          if (this.handleSpecialStatusCode(response)) {
            return;
          }

          if (response.code === 200) {
            // 启动授权检查流程
            this.startAuthorization();
            // 完成后进行重置表单等过程
            this.form.otp = "";
            this.otpError = false;
            // 清除倒计时，让用户可以立即请求新的OTP
            this.stopTimer();

            // 添加提示消息
            this.$refs.alert.show({
              type: "success",
              message: response.message,
            });
          } else {
            this.$refs.alert.show({
              type: "error",
              message: response.message,
            });
          }
        })
        .catch((error) => {
          console.error("secondSubmission failed:", error);
          // 显示错误提示
          this.$refs.alert.show({
            type: "error",
            message: this.$t(`${["网络错误"]}`),
          });
        });
    },

    // 开始授权
    startAuthorization() {
      this.loadAuthorizationStatus = true;
      getAuthStatus(this.selectedOperator, {
        accessKey: this.$store.state.accessKey,
        operator: this.selectedOperator,
        token: this.$store.state.token,
        ...this.form,
      }).then((response) => {
        console.log("[ response ]", "index.vue  475 ->", response);
        if (response.code == 201 || response.code == 200) {
          //开启一个定时器，每秒检查一次授权状态
          this.timer = setInterval(() => {
            this.checkAuthorizationStatus();
          }, 4000);
          //设置超时时间
          setTimeout(() => {
            this.loadAuthorizationStatus = false;
            clearInterval(this.timer);
            this.goToFailPage();
          }, 20000);
        } else if (response.code == 500) {
          this.goToFailPage();
        }
      });
    },

    // 检查授权状态
    checkAuthorizationStatus() {
      getAuthStatus(this.selectedOperator, {
        accessKey: this.$store.state.accessKey,
        operator: this.selectedOperator,
        token: this.$store.state.token,
        ...this.form,
      }).then((response) => {
        console.log("[ response ]", "index.vue  475 ->", response);
        // 授权成功
        if (response.code == 200 && response.status === 2) {
          this.loadAuthorizationStatus = false;
          clearInterval(this.timer);
          this.handleAuthComplete();
          return;
        }
        // 授权失败
        if (response.code == 200 && response.status === 3) {
          this.loadAuthorizationStatus = false;
          clearInterval(this.timer);
          this.goToFailPage();
          return;
        }
      });
    },

    // 停止授权
    stopAuthorization() {
      this.loadAuthorizationStatus = false;
      clearInterval(this.timer);
    },
    //生命周期
    beforeDestroy() {
      clearInterval(this.timer);
      this.stopTimer(); // 清除OTP倒计时
    },

    // 添加 OTP 验证状态处理方法
    handleOtpValidationChange(isValid) {
      this.isOtpValid = isValid;
    },
    // 处理认证完成
    handleAuthComplete() {
      this.authComplete = false;
      window.location.href = this.$route.query.redirectUrl;
    },
    // 添加方法确保OTP验证更加严格
    validateOtpValue(value) {
      // 验证OTP是否存在且为纯数字
      if (!value) return false;

      // 验证是否为数字且满足最小长度要求(通常为4-6位)
      const isNumeric = /^\d+$/.test(value);
      const isValidLength = value.length >= 4;

      return isNumeric && isValidLength;
    },
    // 处理特殊状态码的公共方法
    handleSpecialStatusCode(response) {
      // 处理成功完成验证的情况(code=9527)
      if (response.code == 9527) {
        this.sendDataToParent("success");

        // 清除token
        localStorage.removeItem("token");
        // 跳转到成功页面
        this.goToSuccessPage();

        // 清除倒计时
        this.stopTimer();
        return true; // 表示已处理
      }

      // 处理验证失败的情况(code=50000)
      if (response.code === 50000) {
        this.sendDataToParent("fail");
        this.goToFailPage();

        return true; // 表示已处理
      }

      // 返回false表示未处理特殊状态码
      return false;
    },
  },
  beforeDestroy() {
    clearInterval(this.timer);
    this.stopTimer(); // 清除OTP倒计时
    clearTimeout(this.globalExpireTimer)
  },

};
</script>

<style lang="scss" scoped>
.auth-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-bottom: 20px;
}

.header {
  width: 100%;
  height: 100px;
  background: url("../assets/nen_bg.png");
  background-size: cover;
  background-position: top;
  background-repeat: no-repeat;
  padding-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.header-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 10px;
}

.brand-name {
  font-size: 1.5rem;
  font-weight: bold;
  color: #3b82f6;
  letter-spacing: 0.1em;
}

.subtitle {
  font-size: 0.75rem;
  color: #3b82f6;
}

.auth-form {
  padding: 0 16px;
}

.input-group {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
}

.country-code {
  padding: 10px;
  border-bottom: 2px solid #3b82f6;
  color: #4b5563;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
}

.phone-input {
  flex: 1;
  border: none;
  border-bottom: 2px solid #3b82f6;
  padding: 10px;
  color: #4b5563;
  outline: none;
}

.operator-selector {
  border-bottom: 2px solid #3b82f6;
  padding: 10px;
  margin-left: 8px;
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
}

.selected {
  color: #3b82f6;
}

.not-selected {
  color: #ef4444;
}

.submit-button {
  width: 100%;
  background-color: #3b82f6;
  color: white;
  padding: 12px;
  border-radius: 6px;
  border: none;
  border-bottom: 4px solid #2563eb;
  text-align: center;
  cursor: pointer;
  margin-top: 20px;
  font-weight: 500;
  transition: all 0.2s;
}

.submit-button_disabled {
  background-color: #c4d6f3;
  border-bottom: 4px solid #6f798d;
}

.submit-button:hover {
  background-color: #2563eb;
}

.success-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.success-box {
  background: white;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
  width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.success-icon {
  width: 70px;
  color: #3b82f6;
  margin-bottom: 16px;
}

.success-message {
  text-align: center;
  font-size: 1.125rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.success-button {
  background-color: #ebf5ff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  padding: 10px 20px;
  font-weight: bold;
  cursor: pointer;
  text-decoration: underline;
}

.loading-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.loader-container {
  width: 80%;
  max-width: 320px;
}

.loader {
  background: white;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loader label {
  font-size: 1.125rem;
  font-weight: bold;
  margin-bottom: 16px;
  color: #1f2937;
}

.loading-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
}

.loading-bar::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: #10b981;
  border-radius: 4px;
  animation: loading 1.6s alternate infinite;
}

.cancel-button {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
}

@keyframes loading {
  0% {
    left: -25%;
  }

  100% {
    left: 70%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 移除number类型输入框的上下箭头 */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

/* 从SmsCountdown组件整合的样式 */
.sms-container {
  margin-bottom: 16px;
}

.error-message {
  font-size: 0.875rem;
  color: #ef4444;
  margin-bottom: 4px;
}

.countdown-message {
  font-size: 0.875rem;
  color: #3b82f6;
  margin-top: 4px;
  text-align: right;
}

.countdown-time {
  font-weight: bold;
  color: #3b82f6;
  letter-spacing: 0.05em;
}

.otp-input-group {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.otp-input {
  flex: 1;
  padding: 10px;
  border: none;
  border-bottom: 2px solid #3b82f6;
  outline: none;
  font-size: 1rem;
  color: #4b5563;
  background-color: transparent;
}

.otp-button-container {
  position: relative;
  margin-left: 12px;
}

.otp-button {
  position: relative;
  padding: 8px 16px;
  font-size: 0.875rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 90px;
  text-align: center;
  z-index: 1;
}

.with-countdown {
  padding-right: 36px;
  /* 为环形计时器腾出空间 */
}

.enabled {
  color: white;
  background-color: #3b82f6;
  border: 1px solid #3b82f6;
  animation: pulse 1.5s ease-in-out 3s forwards, shake 1.5s ease-in-out forwards;
  transform: scale(1.3);
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
  }

  100% {
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.8);
  }
}

@keyframes shake {

  0%,
  100% {
    transform: translateX(0);
  }

  10%,
  20%,
  30%,
  40%,
  50%,
  60%,
  70%,
  80%,
  90% {
    transform: translateX(-5px);
  }

  15%,
  25%,
  35%,
  45%,
  55%,
  65%,
  75%,
  85%,
  95% {
    transform: translateX(5px);
  }
}

.disabled {
  color: #9ca3af;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  cursor: not-allowed;
  opacity: 0.7;
}

/* 环形倒计时样式 */
.countdown-circle {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%) rotate(-90deg);
  width: 20px;
  height: 20px;
  z-index: 2;
}

.countdown-circle-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.3);
  stroke-width: 2.5;
}

.countdown-circle-fill {
  fill: none;
  stroke: white;
  stroke-width: 2.5;
  stroke-linecap: round;
  transition: stroke-dasharray 0.3s ease;
}
</style>
